import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { Plus, Trash2 } from 'lucide-react';
import {
  CUSTOMER_AGENT_BUDGET_URL,
  describeError,
  describeFieldError,
  getCustomerAgentBudget,
  isRouteMissing,
  listMetaMessageRates,
  META_RATES_URL,
  problemFieldErrors,
  ROUTE_MISSING_MESSAGE,
  saveCustomerAgentBudget,
  saveMetaMessageRates,
  type CustomerAgentBudgetCap,
  type CustomerAgentBudgetResponse,
  type MetaMessageRate,
  type MetaMessageRatesResponse,
  type SaveCustomerAgentBudgetPayload,
  type SaveMetaMessageRatesPayload,
} from '@/lib/api';
import {
  formatBrlPerUsd,
  formatDateTime,
  formatIsoDate,
  formatMonth,
  formatUsd,
  formatUsdRate,
  META_CATEGORY_PATTERN,
  META_CATEGORY_SUGGESTIONS,
  parseDecimalInput,
} from '@/lib/ai-units';
import { useIdempotencyKey } from '@/lib/idempotency';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';
import { Badge, Tabs, type BadgeTone } from '@/components/ui';

/**
 * "Atendente IA: custos" (`/atendente-ia`, só Admin; change billing-ai-units, decisões 23, 24 e
 * 27; requisito "Página de custos do Atendente IA" de `platform-backoffice`).
 *
 * Duas abas hoje, e a página nasce em abas para a `whatsapp-attendant-launch` acrescentar "Uso" e
 * "Margem":
 *
 * - **Tarifas da Meta**: a tabela por categoria (vigente, agendadas e histórico, com o câmbio de
 *   referência) e o cadastro de uma vigência nova. A tarifa alimenta só a estimativa que o
 *   restaurante vê em "Quem cobra o quê"; a cobrança oficial é da Meta.
 * - **Tetos em dólar**: o teto global e o por loja, cada um com o efetivo, a sobreposição gravada
 *   aqui e o padrão de configuração, mais o gasto estimado do mês na plataforma. Apagar a
 *   sobreposição volta ao padrão. A rota entra com o módulo do atendente: até lá, a aba diz que
 *   esta versão da API ainda não a tem.
 *
 * Nada aqui é por loja: as duas rotas são globais e não devolvem id de tenant.
 */

type CostsTab = 'tarifas' | 'tetos';

const TABS: Array<{ value: CostsTab; label: string }> = [
  { value: 'tarifas', label: 'Tarifas da Meta' },
  { value: 'tetos', label: 'Tetos em dólar' },
];

export function CustomerAgentCostsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: CostsTab = searchParams.get('aba') === 'tetos' ? 'tetos' : 'tarifas';

  function selectTab(value: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('aba', value);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <div className="page">
      <h1 style={{ marginTop: 0 }}>Atendente IA: custos</h1>
      <p className="muted" style={{ marginTop: 0 }}>
        Valores da plataforma inteira, sem dado de nenhuma loja.
      </p>
      <div style={{ marginBottom: 16 }}>
        <Tabs ariaLabel="Custos do Atendente IA" value={tab} onChange={selectTab} tabs={TABS} />
      </div>
      {tab === 'tarifas' ? <MetaRatesTab /> : <BudgetTab />}
    </div>
  );
}

// ===========================================================================
//  Tarifas da Meta
// ===========================================================================

function rateSituation(rate: MetaMessageRate, current: MetaMessageRate | null): { label: string; tone: BadgeTone } {
  if (current && rate.id === current.id) return { label: 'Vigente', tone: 'success' };
  if (!current || rate.effectiveFrom > current.effectiveFrom) return { label: 'Agendada', tone: 'info' };
  return { label: 'Anterior', tone: 'neutral' };
}

function MetaRatesTab() {
  const [table, setTable] = useState<MetaMessageRatesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [formNonce, setFormNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listMetaMessageRates()
      .then((data) => !cancelled && setTable(data))
      .catch((err) => !cancelled && setError(isRouteMissing(err) ? ROUTE_MISSING_MESSAGE : describeError(err)));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section aria-label="Tarifas da Meta">
      <div className="info-banner" style={{ marginBottom: 16 }} data-testid="meta-rates-note">
        A tarifa da Meta alimenta <strong>só uma estimativa</strong>, mostrada ao restaurante em "Quem cobra o quê" no
        painel. A cobrança oficial das mensagens é da Meta, no cartão do WhatsApp Business da loja; o Vapt Menu não cobra
        mensagem. Categoria sem tarifa aparece ao dono só com a contagem e "Tarifa ainda não cadastrada".
      </div>

      {notice && (
        <div className="info-banner" role="status" style={{ marginBottom: 16 }}>
          {notice}
        </div>
      )}
      {error && !table && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}
      {!table && !error && <p className="muted">Carregando…</p>}

      {table && (
        <>
          <CurrentRatesCard table={table} />
          <NewRatesForm
            key={formNonce}
            table={table}
            onSaved={(saved, next) => {
              setTable(next);
              setFormNonce((n) => n + 1);
              const day = saved[0] ? formatIsoDate(saved[0].effectiveFrom) : '';
              setNotice(
                `Vigência de ${day} cadastrada para ${saved.length === 1 ? '1 categoria' : `${saved.length} categorias`}.`,
              );
            }}
          />
          <RatesHistoryCard table={table} />
        </>
      )}
    </section>
  );
}

function CurrentRatesCard({ table }: { table: MetaMessageRatesResponse }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2 style={{ marginTop: 0, fontSize: 15 }}>Tarifa por categoria</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Em dólar por mensagem cobrável, como a Meta manda em <span className="mono">pricing.category</span>. Hoje em
        São Paulo: {formatIsoDate(table.today)}.
      </p>
      {table.categories.length === 0 ? (
        <p className="muted" style={{ margin: 0 }} data-testid="meta-rates-empty">
          Nenhuma tarifa cadastrada. Até o cadastro, o painel mostra só a contagem de mensagens e "Tarifa ainda não
          cadastrada".
        </p>
      ) : (
        <div className="table-scroll">
          <CardTable aria-label="Tarifa vigente por categoria">
            <THead>
              <Tr>
                <Th>Categoria</Th>
                <Th>Vigente</Th>
                <Th>Desde</Th>
                <Th>Câmbio de referência</Th>
                <Th>Agendadas</Th>
              </Tr>
            </THead>
            <TBody>
              {table.categories.map((category) => (
                <Tr key={category.category} data-testid={`meta-rate-${category.category}`}>
                  <Td heading className="mono">
                    {category.category}
                  </Td>
                  <Td label="Vigente">
                    {category.current ? (
                      `${formatUsdRate(category.current.usdPerMessage)} por mensagem`
                    ) : (
                      <span className="muted">Sem tarifa vigente</span>
                    )}
                  </Td>
                  <Td label="Desde">{category.current ? formatIsoDate(category.current.effectiveFrom) : '—'}</Td>
                  <Td label="Câmbio de referência">
                    {category.current?.referenceBrlPerUsd != null ? (
                      formatBrlPerUsd(category.current.referenceBrlPerUsd)
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </Td>
                  <Td label="Agendadas">
                    {category.scheduled.length === 0 ? (
                      <span className="muted">Nenhuma</span>
                    ) : (
                      category.scheduled.map((rate) => (
                        <div key={rate.id} data-testid="meta-rate-scheduled">
                          {formatUsdRate(rate.usdPerMessage)} a partir de {formatIsoDate(rate.effectiveFrom)}
                        </div>
                      ))
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </CardTable>
        </div>
      )}
    </div>
  );
}

function RatesHistoryCard({ table }: { table: MetaMessageRatesResponse }) {
  const rows = table.categories.flatMap((category) => category.versions.map((rate) => ({ category, rate })));
  return (
    <div className="card">
      <h2 style={{ marginTop: 0, fontSize: 15 }}>Histórico de vigências</h2>
      {rows.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          Nenhuma vigência registrada.
        </p>
      ) : (
        <div className="table-scroll">
          <CardTable aria-label="Histórico de tarifas da Meta">
            <THead>
              <Tr>
                <Th>Categoria</Th>
                <Th>Vigência</Th>
                <Th>Por mensagem</Th>
                <Th>Câmbio de referência</Th>
                <Th>Situação</Th>
                <Th>Cadastrada em</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map(({ category, rate }) => {
                const situation = rateSituation(rate, category.current);
                return (
                  <Tr key={rate.id}>
                    <Td heading className="mono">
                      {rate.category}
                    </Td>
                    <Td label="Vigência">{formatIsoDate(rate.effectiveFrom)}</Td>
                    <Td label="Por mensagem">{formatUsdRate(rate.usdPerMessage)}</Td>
                    <Td label="Câmbio de referência">
                      {rate.referenceBrlPerUsd != null ? formatBrlPerUsd(rate.referenceBrlPerUsd) : '—'}
                    </Td>
                    <Td label="Situação">
                      <Badge tone={situation.tone}>{situation.label}</Badge>
                    </Td>
                    <Td label="Cadastrada em" className="muted">
                      {formatDateTime(rate.createdAt)}
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </CardTable>
        </div>
      )}
    </div>
  );
}

/** Os mesmos limites da API (`MetaMessageRateRules`). */
const MAX_RATES_PER_REQUEST = 20;
const USD_DECIMALS = 6;
const BRL_PER_USD_DECIMALS = 4;

interface RateRow {
  id: number;
  category: string;
  usd: string;
}

function rowError(row: RateRow, rows: RateRow[]): string | null {
  const category = row.category.trim();
  if (category && !META_CATEGORY_PATTERN.test(category)) {
    return 'A categoria deve ter de 3 a 40 letras minúsculas ou sublinhados, como a Meta a manda.';
  }
  if (category && rows.filter((r) => r.category.trim() === category).length > 1) {
    return 'A categoria está repetida no pedido.';
  }
  if (row.usd.trim() && parseDecimalInput(row.usd, USD_DECIMALS).state !== 'ok') {
    return 'A tarifa deve ser um número de zero em diante, com até seis casas decimais.';
  }
  return null;
}

function NewRatesForm({
  table,
  onSaved,
}: {
  table: MetaMessageRatesResponse;
  onSaved: (saved: MetaMessageRate[], next: MetaMessageRatesResponse) => void;
}) {
  const [effectiveFrom, setEffectiveFrom] = useState(table.today);
  const [brlPerUsd, setBrlPerUsd] = useState('');
  const [rows, setRows] = useState<RateRow[]>([{ id: 0, category: '', usd: '' }]);
  const [nextId, setNextId] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();

  const suggestions = useMemo(
    () => Array.from(new Set([...META_CATEGORY_SUGGESTIONS, ...table.categories.map((c) => c.category)])),
    [table],
  );

  const brl = parseDecimalInput(brlPerUsd, BRL_PER_USD_DECIMALS);
  const brlError =
    brl.state === 'invalid' || (brl.state === 'ok' && brl.value <= 0)
      ? 'O câmbio de referência deve ser maior que zero, com até quatro casas decimais.'
      : null;
  const dateError = effectiveFrom && effectiveFrom < table.today ? 'A vigência deve ser de hoje em diante.' : null;
  const rowErrors = rows.map((row) => rowError(row, rows));
  const complete = rows.every((row) => row.category.trim() !== '' && row.usd.trim() !== '');
  const valid = complete && !brlError && !dateError && rowErrors.every((e) => e === null);

  function updateRow(id: number, patch: Partial<RateRow>) {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function addRow() {
    if (rows.length >= MAX_RATES_PER_REQUEST) return;
    setRows((prev) => [...prev, { id: nextId, category: '', usd: '' }]);
    setNextId((n) => n + 1);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || submitting) return;
    const payload: SaveMetaMessageRatesPayload = {
      effectiveFrom: effectiveFrom || null,
      referenceBrlPerUsd: brl.state === 'ok' ? brl.value : null,
      rates: rows.map((row) => {
        const usd = parseDecimalInput(row.usd, USD_DECIMALS);
        return { category: row.category.trim(), usdPerMessage: usd.state === 'ok' ? usd.value : 0 };
      }),
    };
    const key = idempotency.keyFor(META_RATES_URL, payload);
    setSubmitting(true);
    setFieldErrors({});
    setError(null);
    try {
      const saved = await saveMetaMessageRates(payload, { idempotencyKey: key });
      idempotency.settle();
      onSaved(saved.saved, saved.table);
    } catch (err) {
      idempotency.settle(err);
      const byField = problemFieldErrors(err);
      if (Object.keys(byField).length > 0) {
        setFieldErrors(byField);
        if (byField.rates) setError(byField.rates[0]);
      } else {
        setError(describeError(err));
      }
      setSubmitting(false);
    }
  }

  const serverError = (field: string) =>
    fieldErrors[field]?.length ? (
      <div className="error-banner" role="alert" style={{ marginTop: 6, padding: '8px 12px' }} data-testid={`rate-error-${field}`}>
        {fieldErrors[field][0]}
      </div>
    ) : null;

  return (
    <form className="card" style={{ marginBottom: 16 }} onSubmit={submit} aria-label="Nova vigência de tarifas">
      <h2 style={{ marginTop: 0, fontSize: 15 }}>Nova vigência</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        A vigência começa no dia escolhido, de hoje em diante, e vale para todas as categorias do envio. A tarifa
        anterior de cada categoria continua valendo até lá. Para corrigir uma vigência, cadastre outra a partir do dia
        seguinte.
      </p>
      <div className="grid-2">
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="meta-effective-from">Vigência a partir de</label>
          <input
            id="meta-effective-from"
            type="date"
            min={table.today}
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            aria-invalid={dateError || fieldErrors.effectiveFrom ? true : undefined}
          />
          {dateError && <div className="muted" role="status" style={{ fontSize: 12 }}>{dateError}</div>}
          {serverError('effectiveFrom')}
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="meta-brl-per-usd">Câmbio de referência (R$ por US$, opcional)</label>
          <input
            id="meta-brl-per-usd"
            inputMode="decimal"
            placeholder="5,4321"
            value={brlPerUsd}
            onChange={(e) => setBrlPerUsd(e.target.value)}
            aria-invalid={brlError || fieldErrors.referenceBrlPerUsd ? true : undefined}
          />
          <div className="muted" style={{ fontSize: 12 }}>
            Sem câmbio, o dono vê a estimativa só em dólar.
          </div>
          {brlError && <div className="muted" role="status" style={{ fontSize: 12 }}>{brlError}</div>}
          {serverError('referenceBrlPerUsd')}
        </div>
      </div>

      <datalist id="meta-categories">
        {suggestions.map((category) => (
          <option key={category} value={category} />
        ))}
      </datalist>

      <div style={{ marginTop: 12 }}>
        {rows.map((row, index) => (
          <div key={row.id} className="grid-2" style={{ alignItems: 'end', marginBottom: 8 }} data-testid="meta-rate-row">
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor={`meta-category-${row.id}`}>Categoria</label>
              <input
                id={`meta-category-${row.id}`}
                list="meta-categories"
                value={row.category}
                onChange={(e) => updateRow(row.id, { category: e.target.value })}
                placeholder="service"
                autoComplete="off"
                spellCheck={false}
                aria-invalid={fieldErrors[`rates[${index}].category`] ? true : undefined}
              />
              {serverError(`rates[${index}].category`)}
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor={`meta-usd-${row.id}`}>US$ por mensagem</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  id={`meta-usd-${row.id}`}
                  inputMode="decimal"
                  placeholder="0,0068"
                  value={row.usd}
                  onChange={(e) => updateRow(row.id, { usd: e.target.value })}
                  aria-invalid={fieldErrors[`rates[${index}].usdPerMessage`] ? true : undefined}
                />
                {rows.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setRows((prev) => prev.filter((r) => r.id !== row.id))}
                    aria-label={`Remover a linha ${index + 1}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              {serverError(`rates[${index}].usdPerMessage`)}
            </div>
            {rowErrors[index] && (
              <p className="muted" role="status" style={{ gridColumn: '1 / -1', margin: 0, fontSize: 12 }}>
                {rowErrors[index]}
              </p>
            )}
          </div>
        ))}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={addRow}
          disabled={rows.length >= MAX_RATES_PER_REQUEST}
        >
          <Plus size={14} /> Adicionar categoria
        </button>
      </div>

      {error && (
        <div className="error-banner" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button type="submit" className="btn" disabled={!valid || submitting}>
          {submitting ? 'Cadastrando…' : 'Cadastrar vigência'}
        </button>
      </div>
    </form>
  );
}

// ===========================================================================
//  Tetos em dólar
// ===========================================================================

/** Os mesmos limites da API (decisão 23 do design). */
const MIN_BUDGET_USD = 1;
const MAX_BUDGET_USD = 100_000;
const BUDGET_DECIMALS = 2;

type BudgetDraft = { state: 'empty' } | { state: 'invalid' } | { state: 'ok'; value: number };

function parseBudget(raw: string): BudgetDraft {
  const parsed = parseDecimalInput(raw, BUDGET_DECIMALS);
  if (parsed.state !== 'ok') return parsed;
  return parsed.value >= MIN_BUDGET_USD && parsed.value <= MAX_BUDGET_USD ? parsed : { state: 'invalid' };
}

function overrideText(value: number | null): string {
  return value === null ? '' : String(value).replace('.', ',');
}

function BudgetTab() {
  const [budget, setBudget] = useState<CustomerAgentBudgetResponse | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [formNonce, setFormNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getCustomerAgentBudget()
      .then((data) => {
        if (cancelled) return;
        if (data === null) {
          setState('missing');
          return;
        }
        setBudget(data);
        setState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(describeError(err));
        setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section aria-label="Tetos em dólar">
      <div className="info-banner" style={{ marginBottom: 16 }} data-testid="budget-note">
        Os tetos limitam o custo estimado do Atendente IA no mês, antes de chamar o modelo: o global vale para a
        plataforma inteira, e o por loja vale para cada loja. A sobreposição gravada aqui vale em até 60 segundos, sem
        deploy, e <strong>apagar a sobreposição volta ao padrão de configuração</strong>.
      </div>

      {notice && (
        <div className="info-banner" role="status" style={{ marginBottom: 16 }}>
          {notice}
        </div>
      )}
      {state === 'loading' && <p className="muted">Carregando…</p>}
      {state === 'error' && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}
      {state === 'missing' && (
        <p className="muted" data-testid="budget-missing">
          {ROUTE_MISSING_MESSAGE} Os tetos em dólar entram com o módulo do Atendente IA; até lá, o atendente usa o padrão
          de configuração.
        </p>
      )}

      {state === 'ready' && budget && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="muted" style={{ fontSize: 14 }}>
              Gasto estimado em {formatMonth(budget.month)}
            </div>
            <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: 28, marginTop: 4 }} data-testid="budget-spent">
              {formatUsd(budget.estimatedSpentUsd)}
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
              Na plataforma inteira, todas as lojas somadas, pelo preço do modelo.
              {budget.global.effectiveUsd > 0 &&
                ` ${Math.round((budget.estimatedSpentUsd / budget.global.effectiveUsd) * 100)}% do teto global.`}
            </div>
          </div>

          <div className="grid-halves" style={{ marginBottom: 16 }}>
            <BudgetCapCard title="Teto global (plataforma, por mês)" cap={budget.global} testId="budget-global" />
            <BudgetCapCard title="Teto por loja (cada loja, por mês)" cap={budget.perTenant} testId="budget-per-tenant" />
          </div>

          <BudgetForm
            key={formNonce}
            budget={budget}
            onSaved={(next) => {
              setBudget(next);
              setFormNonce((n) => n + 1);
              setNotice('Tetos gravados. O atendente passa a usar os valores efetivos novos em até 60 segundos.');
            }}
          />
        </>
      )}
    </section>
  );
}

function BudgetCapCard({ title, cap, testId }: { title: string; cap: CustomerAgentBudgetCap; testId: string }) {
  return (
    <div className="card" data-testid={testId}>
      <div className="card-title" style={{ marginBottom: 8 }}>
        {title}
      </div>
      <div className="detail-grid">
        <div className="k">Efetivo</div>
        <div style={{ fontWeight: 700 }} data-testid={`${testId}-effective`}>
          {formatUsd(cap.effectiveUsd)}
        </div>
        <div className="k">Sobreposição</div>
        <div data-testid={`${testId}-override`}>
          {cap.overrideUsd === null ? <span className="muted">Nenhuma: vale o padrão</span> : formatUsd(cap.overrideUsd)}
        </div>
        <div className="k">Padrão de configuração</div>
        <div data-testid={`${testId}-default`}>{formatUsd(cap.defaultUsd)}</div>
      </div>
    </div>
  );
}

function BudgetForm({
  budget,
  onSaved,
}: {
  budget: CustomerAgentBudgetResponse;
  onSaved: (next: CustomerAgentBudgetResponse) => void;
}) {
  const [globalText, setGlobalText] = useState(overrideText(budget.global.overrideUsd));
  const [tenantText, setTenantText] = useState(overrideText(budget.perTenant.overrideUsd));
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();

  const global = parseBudget(globalText);
  const perTenant = parseBudget(tenantText);
  const effectiveGlobal = global.state === 'ok' ? global.value : budget.global.defaultUsd;
  const effectiveTenant = perTenant.state === 'ok' ? perTenant.value : budget.perTenant.defaultUsd;

  const rangeMessage = 'Informe de US$ 1 a US$ 100.000, com até duas casas, ou deixe em branco para voltar ao padrão.';
  const globalError = global.state === 'invalid' ? rangeMessage : null;
  const tenantError = perTenant.state === 'invalid' ? rangeMessage : null;
  const orderError =
    !globalError && !tenantError && effectiveTenant > effectiveGlobal
      ? `O teto por loja (${formatUsd(effectiveTenant)}) não pode passar do global (${formatUsd(effectiveGlobal)}).`
      : null;
  const valid = !globalError && !tenantError && !orderError;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || submitting) return;
    const payload: SaveCustomerAgentBudgetPayload = {
      globalMonthlyUsd: global.state === 'ok' ? global.value : null,
      tenantMonthlyUsd: perTenant.state === 'ok' ? perTenant.value : null,
    };
    const key = idempotency.keyFor(CUSTOMER_AGENT_BUDGET_URL, payload);
    setSubmitting(true);
    setFieldErrors({});
    setError(null);
    try {
      const next = await saveCustomerAgentBudget(payload, { idempotencyKey: key });
      idempotency.settle();
      onSaved(next);
    } catch (err) {
      idempotency.settle(err);
      const byField = problemFieldErrors(err);
      if (Object.keys(byField).length > 0) {
        setFieldErrors(byField);
        const known = ['globalMonthlyUsd', 'tenantMonthlyUsd'];
        if (Object.keys(byField).some((field) => !known.includes(field))) setError(describeFieldError(err));
      } else {
        setError(describeError(err));
      }
      setSubmitting(false);
    }
  }

  const serverError = (field: string) =>
    fieldErrors[field]?.length ? (
      <div className="error-banner" role="alert" style={{ marginTop: 6, padding: '8px 12px' }} data-testid={`budget-error-${field}`}>
        {fieldErrors[field][0]}
      </div>
    ) : null;

  return (
    <form className="card" onSubmit={submit} aria-label="Sobreposição dos tetos">
      <h2 style={{ marginTop: 0, fontSize: 15 }}>Sobreposição dos tetos</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Deixe o campo em branco para apagar a sobreposição e voltar ao padrão de configuração. O teto por loja nunca
        fica acima do global; a última gravação vale.
      </p>
      <div className="grid-2">
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="budget-global-input">Teto global (US$ por mês)</label>
          <input
            id="budget-global-input"
            inputMode="decimal"
            placeholder={`padrão: ${String(budget.global.defaultUsd).replace('.', ',')}`}
            value={globalText}
            onChange={(e) => setGlobalText(e.target.value)}
            aria-invalid={globalError || fieldErrors.globalMonthlyUsd ? true : undefined}
          />
          {globalError && <div className="muted" role="status" style={{ fontSize: 12 }}>{globalError}</div>}
          {serverError('globalMonthlyUsd')}
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="budget-tenant-input">Teto por loja (US$ por mês)</label>
          <input
            id="budget-tenant-input"
            inputMode="decimal"
            placeholder={`padrão: ${String(budget.perTenant.defaultUsd).replace('.', ',')}`}
            value={tenantText}
            onChange={(e) => setTenantText(e.target.value)}
            aria-invalid={tenantError || fieldErrors.tenantMonthlyUsd ? true : undefined}
          />
          {tenantError && <div className="muted" role="status" style={{ fontSize: 12 }}>{tenantError}</div>}
          {serverError('tenantMonthlyUsd')}
        </div>
      </div>
      <p className="muted" style={{ marginBottom: 0, fontSize: 13 }} data-testid="budget-preview">
        Depois de gravar: global {formatUsd(effectiveGlobal)}
        {global.state !== 'ok' && ' (padrão)'}, por loja {formatUsd(effectiveTenant)}
        {perTenant.state !== 'ok' && ' (padrão)'}.
      </p>
      {orderError && (
        <p className="muted" role="status" style={{ margin: '8px 0 0' }}>
          {orderError}
        </p>
      )}
      {error && (
        <div className="error-banner" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <button type="submit" className="btn" disabled={!valid || submitting}>
          {submitting ? 'Gravando…' : 'Gravar tetos'}
        </button>
      </div>
    </form>
  );
}
